import ApplicationController from "./application_controller";
import { Target, Targets } from "@vytant/stimulus-decorators";
import { debounce, DebouncedFunc } from "lodash-es";
import { cloneElement } from "../helpers/dom";

const dropzoneClasses = ["border", "border-dashed", "border-slate-400", "bg-gray-50"];
const dragTargetClasses = ["shadow-sm"];
const dropTargetClasses = ["shadow-inner", "shadow-sm"];

// inspiration from https://onrails.blog/2024/03/08/hotwire-tutorial-how-do-i-drag-and-drop-items-in-a-list/
export default class GenerationOutlineController extends ApplicationController {
  @Targets draggableTargets!: Array<HTMLElement>;
  @Target headingsContainerTarget!: HTMLElement;
  @Target headingTemplateTarget!: HTMLTemplateElement;

  dragItem: HTMLElement | undefined;
  debouncedDispatch!: DebouncedFunc<() => void>;

  initialize() {
    this.debouncedDispatch = debounce(() => this.dispatch("changed"), 500);
  }

  dragstart(event: StimulusEvent & DragEvent) {
    const draggableTarget = this.closestDraggable(event.target);
    if (draggableTarget) {
      this.dragItem = draggableTarget;
      this.element.classList.add(...dropzoneClasses);
      this.dragItem.classList.add(...dragTargetClasses);
      event.dataTransfer!.setDragImage(this.dragItem, 10, 10);
      event.dataTransfer!.effectAllowed = "move";
    }
  }

  dragenter(event: StimulusEvent & DragEvent) {
    const enteredHeading = this.closestDraggable(event.target);
    if (this.dragItem && enteredHeading) {
      event.preventDefault();
      enteredHeading.classList.add(...dropTargetClasses);

      if (enteredHeading.compareDocumentPosition(this.dragItem) & Node.DOCUMENT_POSITION_FOLLOWING) {
        enteredHeading.insertAdjacentElement("beforebegin", this.dragItem);
      } else if (enteredHeading.compareDocumentPosition(this.dragItem) & Node.DOCUMENT_POSITION_PRECEDING) {
        enteredHeading.insertAdjacentElement("afterend", this.dragItem);
      }
    }
  }

  dragleave(event: StimulusEvent & DragEvent) {
    const leftHeading = this.closestDraggable(event.target);
    if (leftHeading) {
      event.preventDefault();
      leftHeading.classList.remove(...dropTargetClasses);
    }
  }

  dragover(event: StimulusEvent & DragEvent) {
    event.preventDefault();
    event.dataTransfer!.dropEffect = "move";
    return true;
  }

  dragend() {
    this.element.classList.remove(...dropzoneClasses);
    if (this.dragItem) {
      this.dragItem.classList.remove(...dragTargetClasses);
    }
  }

  drop() {
    this.element.classList.remove(...dropzoneClasses);
    if (this.dragItem) {
      this.dragItem.classList.remove(...dragTargetClasses);
      this.dragItem = undefined;
    }
  }

  draggableTargetDisconnected() {
    this.debouncedDispatch();
  }

  draggableTargetConnected() {
    this.debouncedDispatch();
  }

  insertHeading() {
    const newElement = cloneElement(this.headingTemplateTarget.content.firstElementChild!);
    this.headingsContainerTarget.appendChild(newElement);
  }

  removeClosestHeading(event: StimulusEvent) {
    this.closestDraggable(event.target)?.remove();
  }

  private closestDraggable(element: HTMLElement) {
    return this.draggableTargets.find((draggableTarget) => draggableTarget.contains(element));
  }
}
